import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo, useRef } from 'react';
import { useAlert } from 'src/contexts/alert-context';
import ErrorReporter from 'src/monitoring/errorReporter';
import type {
  ApiRequestCurrentGuard,
  ApiRetryPolicy,
} from '../../types/api/api-retry-policy.type';
import {
  exponentialBackoffDelay,
  onRetryLimitReached,
  shouldRetry,
} from '../../utils/api/api-retry.util';
import {
  determineInitialLanguage,
  resolveTranslationValue,
} from '../../utils/i18n.util';
import { useRefreshAuthToken } from '../auth';

/**
 * Shares the existing retry/auth policy with callers that own HTTP batches.
 * @returns Stable callbacks using the current auth and alert providers.
 */
export const useApiRetryPolicy = (): ApiRetryPolicy => {
  const { logout } = useAuth0();
  const { refresh } = useRefreshAuthToken();
  const { showAlert } = useAlert();
  const callbacks = useRef({ logout, refresh, showAlert });
  callbacks.current = { logout, refresh, showAlert };

  const shouldRetryRequest = useCallback(
    (
      failureCount: number,
      error: Error,
      isCurrent?: ApiRequestCurrentGuard
    ) => {
      if (isCurrent && !isCurrent()) return false;

      return shouldRetry(failureCount, error, async (options) => {
        if (isCurrent && !isCurrent()) return;
        try {
          await callbacks.current.refresh(options);
        } catch (refreshError) {
          if (!isCurrent || isCurrent()) {
            ErrorReporter.sendException(refreshError);
          }
        }
      });
    },
    []
  );

  const onTerminalError = useCallback(
    (error: Error, isCurrent?: ApiRequestCurrentGuard) =>
      onRetryLimitReached(
        error,
        callbacks.current.logout,
        callbacks.current.showAlert,
        (key) => resolveTranslationValue(determineInitialLanguage(), key),
        isCurrent
      ),
    []
  );

  return useMemo(
    () => ({
      shouldRetry: shouldRetryRequest,
      retryDelay: exponentialBackoffDelay,
      onTerminalError,
    }),
    [onTerminalError, shouldRetryRequest]
  );
};
